import { createContext, useContext, useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../data/firebase";
import { useAuth } from "./AuthContext";
import { User } from "../data/types";

interface UserContextType {
  userData: User | null;
  loading: boolean;
  error: Error | null;
}

const UserContext = createContext<UserContextType | null>(null);

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setUserData(null);
      setLoading(false);
      return;
    }

    const userRef = doc(db, `users/${currentUser.uid}`);
    const unsubscribe = onSnapshot(
      userRef,
      (doc) => {
        if (doc.exists()) {
          setUserData(doc.data() as User);
        } else {
          setError(new Error("User document does not exist"));
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching user data:", error);
        setError(error as Error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [currentUser]);

  return (
    <UserContext.Provider value={{ userData, loading, error }}>
      {children}
    </UserContext.Provider>
  );
}
